import { Box, Icon, type IconProps } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import * as styles from './health-score-gauge.css';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useEnvHealth } from '../api/get-env-health';
import { useWorkspaceHealthSimulation } from '../store';
import { useWorkspaceDetails } from '@/features/masquerade-banner/hooks';
import { isEmptyString } from '@/utils/string';

const SCORE_COLORS_LARGE = {
  // when the status is healthy, we don't want the arc to be visible
  green: {
    arc: themeVars.palette['green-300'],
    track: themeVars.palette['green-300'],
  },
  yellow: {
    arc: themeVars.color['icon-caution'],
    track: themeVars.color['border-minimal'],
  },
  red: {
    arc: themeVars.color['icon-critical'],
    track: themeVars.color['border-base'],
  },
};

const SCORE_COLORS_SMALL = {
  // when the status is healthy, we don't want the arc to be visible
  green: {
    arc: themeVars.palette['green-300'],
    track: themeVars.palette['green-300'],
  },
  yellow: {
    arc: themeVars.color['icon-caution'],
    track: themeVars.color['border-minimal'],
  },
  red: {
    arc: themeVars.color['icon-critical'],
    track: themeVars.color['border-base'],
  },
};

const SCORE_ICON: Record<string, { name: 'check-circle' | 'warning'; color: 'success' | 'caution' | 'critical' }> = {
  green: { name: 'check-circle', color: 'success' },
  yellow: { name: 'warning', color: 'caution' },
  red: { name: 'warning', color: 'critical' },
};

function scoreColor(score: number) {
  if (score >= 80) return 'green';
  if (score >= 30) return 'yellow';
  return 'red';
}

export function HealthScoreGauge({
  size,
  stroke,
  showIcon = true,
  iconSize = 'lg',
}: {
  size?: number;
  stroke?: number;
  showIcon?: boolean;
  iconSize?: IconProps['size'];
}) {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const workspaceDetailsQuery = useWorkspaceDetails(workspaceId);
  const isIdentifiersMigrationInProgress =
    isEmptyString(workspaceDetailsQuery.data?.identifiers_migration_in_progress) === false;

  const data = healthQuery.data?.body;
  const score = data?.score ?? 100;

  const _size = size ?? 84;
  const _stroke = stroke ?? 6;
  const _radius = (_size - _stroke) / 2;
  const _circumference = 2 * Math.PI * _radius;

  const color = scoreColor(score);
  const colorSet = _size < 30 ? SCORE_COLORS_SMALL : SCORE_COLORS_LARGE;
  const colors = colorSet[color];

  const icon = {
    name: isIdentifiersMigrationInProgress ? ('pause' as const) : SCORE_ICON[color].name,
    color: SCORE_ICON[color].color,
  };
  const isDegraded = data?.health === 'degraded';
  const busyness = 1 - score / 100;

  const dashOffset = isDegraded ? 0 : _circumference * (1 - busyness);
  const arcColor = colors.arc;
  const trackColor = isDegraded ? colors.arc : colors.track;

  const outlineWidth = 1;
  const outerOutlineRadius = _radius + _stroke / 2 + outlineWidth / 2;
  const innerOutlineRadius = _radius - _stroke / 2 - outlineWidth / 2;
  const outlineColor = themeVars.color['icon-base'];

  // When drawing the outer outline, expand viewBox so the stroke isn't clipped
  const svgSize = showIcon ? _size : _size + 2 * outlineWidth;
  const center = svgSize / 2;

  return (
    <Box className={styles.gaugeContainer} style={{ width: _size, height: _size }}>
      <svg width={_size} height={_size} viewBox={`0 0 ${svgSize} ${svgSize}`}>
        {/* Outer outline (only when icon is hidden) */}
        {!showIcon ? (
          <circle
            cx={center}
            cy={center}
            r={outerOutlineRadius}
            fill="none"
            stroke={outlineColor}
            strokeWidth={outlineWidth}
          />
        ) : null}
        {/* Track circle */}
        <circle cx={center} cy={center} r={_radius} fill="none" stroke={trackColor} strokeWidth={_stroke} />
        {/* Progress arc */}
        {!isDegraded ? (
          <circle
            cx={center}
            cy={center}
            r={_radius}
            fill="none"
            stroke={arcColor}
            strokeWidth={_stroke}
            strokeLinecap="round"
            strokeDasharray={_circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${center} ${center})`}
          />
        ) : null}
        {/* Inner outline */}
        {!showIcon ? (
          <circle
            cx={center}
            cy={center}
            r={innerOutlineRadius}
            fill="none"
            stroke={outlineColor}
            strokeWidth={outlineWidth}
          />
        ) : null}
      </svg>
      {showIcon ? (
        <Box className={styles.gaugeLabel}>
          <Icon name={icon.name} size={iconSize} color={icon.color} />
        </Box>
      ) : null}
    </Box>
  );
}
